import { FormField, Option } from '../types';

/** Field types a pasted value can be written into. Everything else is skipped, not counted. */
const FILLABLE_TYPES = new Set<string>(['text', 'number', 'email', 'tel', 'url', 'textarea', 'date', 'time', 'datetime', 'select', 'radio', 'hidden']);

export type ClipboardAssignment = { key: string; label: string; value: string };
export type ClipboardUnmatched = { key: string; label: string; value: string; reason: string };
export type ClipboardPlan = {
  assignments: ClipboardAssignment[];
  unmatched: ClipboardUnmatched[];
  /** Number of fields that were candidates for a value (fillable, visible, enabled). */
  fieldCount: number;
  /** Pasted values with no field left to take them. */
  extraValues: number;
};

const stripQuotes = (value: string) => (value.length >= 2 && value.startsWith('"') && value.endsWith('"') ? value.slice(1, -1).replace(/""/g, '"') : value);

/**
 * Clipboard text -> one value per field, in order. Newline separated (a copied column). A single
 * line containing tabs is a copied row and is split into its cells instead. An empty entry means
 * "leave this field alone", which keeps the later values aligned with their fields.
 */
export function parseClipboard(text: string): string[] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  while (lines.length && lines[lines.length - 1].trim() === '') lines.pop();
  const entries = lines.length === 1 && lines[0].includes('\t') ? lines[0].split('\t') : lines;
  return entries.map((entry) => stripQuotes(entry.trim()));
}

const sameText = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Decides which pasted value goes into which field. `fields` are the step's visible fields in
 * display order (including runtime-generated ones); disabled/read-only filtering is the caller's
 * job through `isFillable`. `optionsFor` returns the options already loaded for a select/radio.
 */
export function planClipboardFill(
  values: string[],
  fields: FormField[],
  optionsFor: (field: FormField) => Option[] | undefined,
  isFillable: (field: FormField) => boolean = () => true,
): ClipboardPlan {
  const targets = fields.filter((field) => FILLABLE_TYPES.has(field.type) && isFillable(field));
  const assignments: ClipboardAssignment[] = [];
  const unmatched: ClipboardUnmatched[] = [];

  targets.forEach((field, index) => {
    const raw = values[index];
    if (raw === undefined || raw === '') return;
    const label = field.label || field.key;

    if (field.type === 'select' || field.type === 'radio') {
      const options = optionsFor(field);
      // Options that depend on earlier answers load after those are set, so they can't be checked yet.
      if (!options || options.length === 0) {
        assignments.push({ key: field.key, label, value: raw });
        return;
      }
      const option = options.find((o) => sameText(o.value, raw)) || options.find((o) => sameText(o.label, raw));
      if (option) assignments.push({ key: field.key, label, value: option.value });
      else unmatched.push({ key: field.key, label, value: raw, reason: `“${raw}” is not one of the options` });
      return;
    }

    if (field.type === 'number' && !Number.isFinite(Number(raw.replace(/,/g, '')))) {
      unmatched.push({ key: field.key, label, value: raw, reason: `“${raw}” is not a number` });
      return;
    }

    assignments.push({ key: field.key, label, value: field.type === 'number' ? raw.replace(/,/g, '') : raw });
  });

  return { assignments, unmatched, fieldCount: targets.length, extraValues: Math.max(0, values.length - targets.length) };
}
